/**
 * Scan Comparison ("Release Diff") — SD2 shell.
 *
 * Route: /diff/:repoIdentityHash?a=<analysis_id>&b=<analysis_id>
 *
 * Lets a tenant pick two scans of the same repo and compare them.
 * Four panels stacked: Overview / Findings / Agents / ABF Drift.
 * SD2 lays the shell + picker UX + version-skew banner; SD3 fills the
 * Score + Inventory panels; SD4 fills the Findings + ABF Drift panels.
 *
 * Backend contract:
 *   GET /repos/<hash>/scans  → {scans: [{analysis_id, commit_sha_short, ...}]}
 *   GET /scan-diff?a=&b=     → {analysis_a, analysis_b, version_compatibility,
 *                              score_delta, findings_delta, inventory_delta,
 *                              abf_delta, meta}
 *
 * Both endpoints 404 when FEATURE_RELEASE_DIFF is off — page shows the
 * same "not available" empty state in that case.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import InfoIcon from '@mui/icons-material/InfoOutlined'
import { analysisApi } from '../services/api'
import MaturityRadar from '../components/MaturityRadar'
import DriftBadge from '../components/DriftBadge'
import BundleTeaser from '../components/BundleTeaser'
import { BUNDLE_COPY } from '../components/bundleCopy'
import useCanContactVendor from '../hooks/useCanContactVendor'

// ─────────────────────────────────────────────────────────────────────
// Types — mirror the SD1 backend response. Kept inline because they
// are scan-diff-specific and not shared with other pages.
// ─────────────────────────────────────────────────────────────────────

interface ScanPickerRow {
  analysis_id: string
  created_at: string | null
  commit_sha: string | null
  commit_sha_short: string | null
  branch_name: string | null
  ci_run_id: string | null
  ci_provider: string | null
  scan_trigger: string | null
  scanner_version: string | null
  repository_name: string | null
  agents_detected: number
  findings_count: number
  risk_score_overall: number
}

interface VersionCompatibility {
  category: 'same' | 'patch' | 'minor' | 'major'
  scanner: { a: string | null; b: string | null; skew: string }
  abf: { a: string | null; b: string | null; differs: boolean }
  rebaseline_expected: boolean
}

interface ScanDiffResponse {
  analysis_a: ScanPickerRow
  analysis_b: ScanPickerRow
  version_compatibility: VersionCompatibility
  score_delta: {
    overall: { a: number; b: number; delta: number }
    dimensions: Record<string, { a: number; b: number; delta: number }>
  }
  findings_delta: {
    new: FindingDiffRow[]
    fixed: FindingDiffRow[]
    unchanged?: FindingDiffRow[]
    new_count: number
    fixed_count: number
    unchanged_count: number
  }
  inventory_delta: {
    added: InventoryAgent[]
    removed: InventoryAgent[]
    unchanged: InventoryAgent[]
    added_count: number
    removed_count: number
    unchanged_count: number
  }
  abf_delta: AbfVerdictRow[]
  cost_delta: CostDeltaResponse
  cost_estimation_visible: boolean
  meta: { feature_flag: string; computed_at: string }
}

interface CostRange {
  low: number
  high: number
}

interface CostDeltaAgent {
  agent_id_hint: string
  agent_name: string | null
  framework: string | null
  file_path: string | null
  line_number: number | null
  cost_per_request_usd_a: CostRange | null
  cost_per_request_usd_b: CostRange | null
  delta_pct_midpoint: number
  severity: 'warning' | 'critical' | null
}

interface CostDeltaResponse {
  agents: CostDeltaAgent[]
  summary: { agents_compared: number; drift_flagged_count: number }
}

interface InventoryAgent {
  agent_id_hint: string | null
  agent_name: string | null
  framework: string | null
  file_path: string | null
  autonomy_level: string | null
  multi_agent: boolean | null
}

interface FindingDiffRow {
  id: string | null
  rule_id: string | null
  severity: string | null
  title: string | null
  framework: string | null
  category: string | null
  agent_id_hint: string | null
  file_path: string | null
  line_number: number | null
  confidence: number | null
}

interface AbfVerdictRow {
  agent_id: string
  agent_name: string | null
  framework: string | null
  verdict: string
  drift_score: number | null
  summary: string | null
  improvement?: string | null
  component_drifts?: Record<string, any>
  present_in: 'both' | 'a_only' | 'b_only'
}

const DIMENSION_LABELS: Record<string, string> = {
  prompt_engineering: 'Prompt',
  agent_design: 'Design',
  memory_architecture: 'Memory',
  orchestration_soundness: 'Orchestration',
  observability_tracing: 'Observability',
  governance_alignment: 'Governance',
}

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────

function formatScanLabel(row: ScanPickerRow): string {
  const parts: string[] = []
  if (row.commit_sha_short) parts.push(row.commit_sha_short)
  if (row.branch_name) parts.push(row.branch_name)
  if (row.ci_run_id) parts.push(`CI#${row.ci_run_id}`)
  if (row.created_at) {
    const d = new Date(row.created_at)
    parts.push(d.toLocaleString())
  }
  return parts.length ? parts.join(' · ') : row.analysis_id.slice(0, 8)
}

function VersionSkewBanner({ vc }: { vc: VersionCompatibility }) {
  if (vc.category === 'same') return null
  const sa = vc.scanner.a ?? '?'
  const sb = vc.scanner.b ?? '?'
  const aba = vc.abf.a ?? '?'
  const abb = vc.abf.b ?? '?'

  if (vc.category === 'major') {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        <strong>Major scanner / ABF upgrade between these scans</strong> —
        scanner {sa} → {sb}
        {vc.abf.differs ? `, ABF ${aba} → ${abb}` : ''}.
        {vc.rebaseline_expected
          ? ' Per-agent verdicts auto-rebaseline (REBASELINE) at this boundary; findings counts are not directly comparable.'
          : ' Findings detection may have changed materially between versions.'}
      </Alert>
    )
  }
  if (vc.category === 'minor') {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        <strong>Scanner minor version changed</strong> — {sa} → {sb}. Some
        new findings or detected agents may reflect detection improvements,
        not changes in your code.
      </Alert>
    )
  }
  // patch
  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      Scanner patch differs ({sa} → {sb}). Diff is comparable.
    </Alert>
  )
}

function ScanSummaryCard({
  row,
  side,
}: {
  row: ScanPickerRow | undefined
  side: 'A (baseline)' | 'B (current)'
}) {
  if (!row) {
    return (
      <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 0 }}>
        <Typography variant="overline" color="text.secondary">{side}</Typography>
        <Typography variant="body2" color="text.secondary">Pick a scan</Typography>
      </Paper>
    )
  }
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="overline" color="text.secondary">{side}</Typography>
        {row.scanner_version && (
          <Chip
            label={`scanner ${row.scanner_version}`}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        )}
      </Stack>
      <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
        {row.commit_sha_short ?? row.analysis_id.slice(0, 8)}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {row.branch_name ?? '(no branch)'}
        {row.ci_run_id ? ` · CI#${row.ci_run_id}` : ''}
        {row.ci_provider ? ` · ${row.ci_provider}` : ''}
      </Typography>
      {row.created_at && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {new Date(row.created_at).toLocaleString()}
        </Typography>
      )}
      <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
        <Tooltip title="Agents detected">
          <Chip label={`${row.agents_detected} agents`} size="small" />
        </Tooltip>
        <Tooltip title="Findings">
          <Chip label={`${row.findings_count} findings`} size="small" />
        </Tooltip>
        <Tooltip title="Overall risk score">
          <Chip label={`score ${row.risk_score_overall.toFixed(1)}`} size="small" />
        </Tooltip>
      </Stack>
    </Paper>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────

export default function ScanDiffPage() {
  const { repoIdentityHash } = useParams<{ repoIdentityHash: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  const aId = searchParams.get('a') ?? ''
  const bId = searchParams.get('b') ?? ''

  const [scans, setScans] = useState<ScanPickerRow[]>([])
  const [repoName, setRepoName] = useState<string>('')
  const [scansLoading, setScansLoading] = useState(true)
  const [scansError, setScansError] = useState<string | null>(null)
  const [refused, setRefused] = useState(false)
  const canContactVendor = useCanContactVendor()

  const [diff, setDiff] = useState<ScanDiffResponse | null>(null)
  const [diffLoading, setDiffLoading] = useState(false)
  const [diffError, setDiffError] = useState<string | null>(null)
  // Lazy-loaded list of unchanged findings — fetched the first time the
  // user activates that sub-tab inside the Findings panel.
  const [unchangedFindings, setUnchangedFindings] = useState<FindingDiffRow[] | null>(null)
  const [unchangedLoading, setUnchangedLoading] = useState(false)
  const [unchangedError, setUnchangedError] = useState<string | null>(null)

  const [tab, setTab] = useState(0)

  // ── Load repo's scan list ────────────────────────────────────────
  useEffect(() => {
    if (!repoIdentityHash) return
    let cancelled = false
    setScansLoading(true)
    setScansError(null)
    analysisApi
      .listRepoScans(repoIdentityHash, { limit: 50 })
      .then((resp) => {
        if (cancelled) return
        const data = resp.data
        setScans(data.scans ?? [])
        setRepoName(data.repository_name ?? '')

        // Auto-pick defaults when query params absent
        if (!aId && !bId && (data.scans?.length ?? 0) >= 2) {
          const sorted = data.scans as ScanPickerRow[]
          const next = new URLSearchParams(searchParams)
          next.set('a', sorted[1].analysis_id) // previous scan as baseline
          next.set('b', sorted[0].analysis_id) // latest as current
          setSearchParams(next, { replace: true })
        }
      })
      .catch((err) => {
        if (cancelled) return
        const status = err?.response?.status
        if (status === 404) {
          // Two very different causes shared one sentence: "not available for
          // this tenant, or this repo has no scans visible to you". One is a
          // purchase and the other is a scan, and the reader could not tell
          // which applied to them.
          setRefused(true)
        } else {
          setScansError(`Failed to load scan list: ${err?.message ?? 'unknown error'}`)
        }
      })
      .finally(() => {
        if (!cancelled) setScansLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [repoIdentityHash])

  // ── Load diff when both ids set ──────────────────────────────────
  useEffect(() => {
    // Reset lazy-loaded unchanged-findings cache on scan change
    setUnchangedFindings(null)
    setUnchangedError(null)
    if (!aId || !bId) {
      setDiff(null)
      return
    }
    if (aId === bId) {
      setDiff(null)
      setDiffError('Pick two different scans to compare.')
      return
    }
    let cancelled = false
    setDiffLoading(true)
    setDiffError(null)
    analysisApi
      .getScanDiff(aId, bId, false)
      .then((resp) => {
        if (cancelled) return
        setDiff(resp.data as ScanDiffResponse)
      })
      .catch((err) => {
        if (cancelled) return
        const code = err?.response?.data?.detail?.code
        const message =
          err?.response?.data?.detail?.message ??
          err?.response?.data?.detail ??
          err?.message ??
          'Failed to load diff'
        // Friendly copy for each guard
        if (code === 'cross_repo') {
          setDiffError('These two scans belong to different repos — cross-repo diff is not allowed.')
        } else if (code === 'cross_tenant') {
          setDiffError('You do not have access to one of these scans.')
        } else if (code === 'identical_scans') {
          setDiffError('Pick two different scans to compare.')
        } else if (err?.response?.status === 404) {
          setDiffError('One of these scans is not available.')
        } else {
          setDiffError(String(message))
        }
        setDiff(null)
      })
      .finally(() => {
        if (!cancelled) setDiffLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [aId, bId])

  // ── Picker change handlers ───────────────────────────────────────
  const updateParam = (key: 'a' | 'b', value: string) => {
    const next = new URLSearchParams(searchParams)
    next.set(key, value)
    setSearchParams(next, { replace: true })
  }
  const swap = () => {
    if (!aId || !bId) return
    const next = new URLSearchParams(searchParams)
    next.set('a', bId)
    next.set('b', aId)
    setSearchParams(next, { replace: true })
  }
  const copyPermalink = () => {
    void navigator.clipboard.writeText(window.location.href)
  }
  const loadUnchangedFindings = () => {
    if (!aId || !bId || unchangedFindings !== null || unchangedLoading) return
    setUnchangedLoading(true)
    setUnchangedError(null)
    analysisApi
      .getScanDiff(aId, bId, true)
      .then((resp) => {
        const list = (resp.data as ScanDiffResponse).findings_delta.unchanged ?? []
        setUnchangedFindings(list)
      })
      .catch((err) => {
        setUnchangedError(err?.message ?? 'Failed to load unchanged findings')
      })
      .finally(() => setUnchangedLoading(false))
  }

  const branches = useMemo(() => {
    const set = new Set<string>()
    scans.forEach((s) => {
      if (s.branch_name) set.add(s.branch_name)
    })
    return Array.from(set).sort()
  }, [scans])

  const scanA = scans.find((s) => s.analysis_id === aId)
  const scanB = scans.find((s) => s.analysis_id === bId)

  // ── Render ───────────────────────────────────────────────────────

  if (scansLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (refused) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<KeyboardArrowLeft />} onClick={() => navigate('/analyses')} sx={{ mb: 2 }}>
          Back to analyses
        </Button>
        <BundleTeaser
          copy={BUNDLE_COPY.release_comparison}
          canContactVendor={canContactVendor}
        />
      </Box>
    )
  }

  if (scansError) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<KeyboardArrowLeft />} onClick={() => navigate('/analyses')} sx={{ mb: 2 }}>
          Back to analyses
        </Button>
        <Alert severity="info">{scansError}</Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* ── Header ──────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button startIcon={<KeyboardArrowLeft />} onClick={() => navigate('/analyses')}>
          Back to analyses
        </Button>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5">Compare scans</Typography>
          {repoName && (
            <Typography variant="body2" color="text.secondary">
              {repoName}
            </Typography>
          )}
        </Box>
        <Tooltip title="Copy permalink to this comparison">
          <span>
            <IconButton size="small" onClick={copyPermalink} disabled={!aId || !bId}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {scans.length < 2 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Need at least 2 scans to compare. This repo has {scans.length} scan
          {scans.length === 1 ? '' : 's'} visible to you.
        </Alert>
      )}

      {/* ── Picker row ──────────────────────────────────────────── */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel id="picker-a">A (baseline)</InputLabel>
          <Select
            labelId="picker-a"
            label="A (baseline)"
            value={aId}
            onChange={(e) => updateParam('a', String(e.target.value))}
          >
            {scans.map((s) => (
              <MenuItem key={s.analysis_id} value={s.analysis_id}>
                {formatScanLabel(s)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Tooltip title="Swap A and B">
          <span>
            <IconButton onClick={swap} disabled={!aId || !bId} sx={{ alignSelf: 'center' }}>
              <SwapHorizIcon />
            </IconButton>
          </span>
        </Tooltip>

        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel id="picker-b">B (current)</InputLabel>
          <Select
            labelId="picker-b"
            label="B (current)"
            value={bId}
            onChange={(e) => updateParam('b', String(e.target.value))}
          >
            {scans.map((s) => (
              <MenuItem key={s.analysis_id} value={s.analysis_id}>
                {formatScanLabel(s)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {branches.length > 1 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Available branches: {branches.join(', ')}
        </Typography>
      )}

      {/* ── Scan summary header strip ──────────────────────────── */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <ScanSummaryCard row={scanA} side="A (baseline)" />
        <ScanSummaryCard row={scanB} side="B (current)" />
      </Stack>

      {/* ── Diff body ──────────────────────────────────────────── */}
      {diffError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {diffError}
        </Alert>
      )}

      {diffLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {diff && !diffLoading && (
        <>
          <VersionSkewBanner vc={diff.version_compatibility} />

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Overview" />
            <Tab label={`Findings (+${diff.findings_delta.new_count} / −${diff.findings_delta.fixed_count})`} />
            <Tab label={`Agents (+${diff.inventory_delta.added_count} / −${diff.inventory_delta.removed_count})`} />
            <Tab label={`ABF Drift (${diff.abf_delta.length})`} />
            {diff.cost_estimation_visible && (
              <Tab label={`Cost (${diff.cost_delta.summary.drift_flagged_count})`} />
            )}
          </Tabs>

          {/* Tab 0 — Overview: score delta + radar overlay + summary stats */}
          {tab === 0 && (
            <Card variant="outlined">
              <CardContent>
                {/* Headline — overall score with direction */}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="center">
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" color="text.secondary">
                      Overall risk score
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="baseline" sx={{ mb: 1 }}>
                      <Typography variant="h3">
                        {diff.score_delta.overall.b.toFixed(1)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        was {diff.score_delta.overall.a.toFixed(1)}
                      </Typography>
                      <DeltaChip value={diff.score_delta.overall.delta} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      Higher is better (0-10).{' '}
                      {diff.score_delta.overall.delta > 0
                        ? 'Maturity improved between scans.'
                        : diff.score_delta.overall.delta < 0
                        ? 'Maturity regressed between scans.'
                        : 'No change in overall maturity.'}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="overline" color="text.secondary">
                      Per-dimension
                    </Typography>
                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                      {Object.entries(diff.score_delta.dimensions).map(([dim, vals]) => (
                        <Stack key={dim} direction="row" spacing={2} alignItems="center">
                          <Typography variant="body2" sx={{ width: 200 }}>
                            {DIMENSION_LABELS[dim] ?? dim.replace(/_/g, ' ')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ width: 90 }}>
                            {vals.a.toFixed(1)} → {vals.b.toFixed(1)}
                          </Typography>
                          <DeltaChip value={vals.delta} />
                        </Stack>
                      ))}
                    </Stack>
                  </Box>

                  {/* Radar with two polygons — A baseline (dashed grey),
                      B current (filled blue). Visual at-a-glance. */}
                  <Box>
                    <MaturityRadar
                      scores={Object.entries(diff.score_delta.dimensions).map(([k, v]) => ({
                        key: k,
                        label: DIMENSION_LABELS[k] ?? k,
                        score: v.b,
                      }))}
                      previousScores={Object.entries(diff.score_delta.dimensions).map(([k, v]) => ({
                        key: k,
                        label: DIMENSION_LABELS[k] ?? k,
                        score: v.a,
                      }))}
                    />
                    <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: -1 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Box sx={{ width: 12, height: 2, bgcolor: '#9e9e9e', borderTop: '1px dashed #9e9e9e' }} />
                        <Typography variant="caption" color="text.secondary">A (baseline)</Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Box sx={{ width: 12, height: 2, bgcolor: '#1976d2' }} />
                        <Typography variant="caption" color="text.secondary">B (current)</Typography>
                      </Stack>
                    </Stack>
                  </Box>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" spacing={4} flexWrap="wrap">
                  <SummaryStat label="New findings" value={diff.findings_delta.new_count} severity="warning" />
                  <SummaryStat label="Fixed findings" value={diff.findings_delta.fixed_count} severity="success" />
                  <SummaryStat label="Agents added" value={diff.inventory_delta.added_count} severity="info" />
                  <SummaryStat label="Agents removed" value={diff.inventory_delta.removed_count} severity="warning" />
                  {diff.cost_estimation_visible && (
                    <SummaryStat
                      label="Cost drift flagged"
                      value={diff.cost_delta.summary.drift_flagged_count}
                      severity="warning"
                    />
                  )}
                </Stack>

                <Box sx={{ mt: 2 }}>
                  <Link
                    component="button"
                    underline="hover"
                    onClick={() => navigate(`/analyses/${diff.analysis_b.analysis_id}`)}
                  >
                    View topology of current scan →
                  </Link>
                </Box>
              </CardContent>
            </Card>
          )}

          {tab === 1 && (
            <FindingsPanel
              delta={diff.findings_delta}
              unchanged={unchangedFindings}
              unchangedLoading={unchangedLoading}
              unchangedError={unchangedError}
              onLoadUnchanged={loadUnchangedFindings}
              versionCategory={diff.version_compatibility.category}
              onNavigateAgent={(hint) =>
                navigate(`/topology?focus=${encodeURIComponent(hint)}`)
              }
            />
          )}

          {tab === 2 && (
            <InventoryPanel
              delta={diff.inventory_delta}
              onNavigateTopology={(hint) => {
                navigate(`/topology?focus=${encodeURIComponent(hint)}`)
              }}
            />
          )}

          {tab === 3 && (
            <AbfDriftPanel
              rows={diff.abf_delta}
              abfDiffers={diff.version_compatibility.abf.differs}
            />
          )}

          {tab === 4 && diff.cost_estimation_visible && (
            <CostDriftPanel
              rows={diff.cost_delta.agents}
              onNavigateAgent={(hint) =>
                navigate(`/topology?focus=${encodeURIComponent(hint)}`)
              }
            />
          )}
        </>
      )}

      {!diff && !diffLoading && !diffError && aId && bId && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Loading diff…
          </Typography>
        </Box>
      )}

      {(!aId || !bId) && scans.length >= 2 && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Pick two scans above to compare.
          </Typography>
        </Box>
      )}
    </Box>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Small shared bits
// ─────────────────────────────────────────────────────────────────────

function DeltaChip({ value }: { value: number }) {
  if (value === 0) {
    return <Chip label="±0.0" size="small" variant="outlined" />
  }
  const positive = value > 0
  return (
    <Chip
      label={`${positive ? '+' : ''}${value.toFixed(1)}`}
      size="small"
      color={positive ? 'success' : 'warning'}
      variant="outlined"
    />
  )
}

function SummaryStat({
  label,
  value,
  severity,
}: {
  label: string
  value: number
  severity: 'success' | 'warning' | 'info' | 'error'
}) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Chip label={value} color={value === 0 ? 'default' : severity} size="small" />
    </Box>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Inventory panel — Added / Removed / Unchanged agent cards (SD3)
// ─────────────────────────────────────────────────────────────────────

function InventoryPanel({
  delta,
  onNavigateTopology,
}: {
  delta: {
    added: InventoryAgent[]
    removed: InventoryAgent[]
    unchanged: InventoryAgent[]
    added_count: number
    removed_count: number
    unchanged_count: number
  }
  onNavigateTopology: (agentIdHint: string) => void
}) {
  const [showUnchanged, setShowUnchanged] = useState(false)

  if (
    delta.added_count === 0 &&
    delta.removed_count === 0 &&
    delta.unchanged_count === 0
  ) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            No agents detected in either scan.
          </Typography>
        </CardContent>
      </Card>
    )
  }

  return (
    <Stack spacing={2}>
      <InventoryColumn
        title="Added"
        kind="added"
        count={delta.added_count}
        agents={delta.added}
        emptyText="No new agents in this scan."
        onNavigateTopology={onNavigateTopology}
      />
      <InventoryColumn
        title="Removed"
        kind="removed"
        count={delta.removed_count}
        agents={delta.removed}
        emptyText="No agents removed in this scan."
        onNavigateTopology={onNavigateTopology}
      />
      <Card variant="outlined">
        <CardContent>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: showUnchanged ? 1.5 : 0 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="overline" color="text.secondary">
                Unchanged
              </Typography>
              <Chip
                label={delta.unchanged_count}
                size="small"
                color={delta.unchanged_count === 0 ? 'default' : 'default'}
              />
            </Stack>
            <Button
              size="small"
              onClick={() => setShowUnchanged((v) => !v)}
              disabled={delta.unchanged_count === 0}
            >
              {showUnchanged ? 'Hide' : 'Show'}
            </Button>
          </Stack>
          {showUnchanged && (
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 1 }}
            >
              {delta.unchanged.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No agents carried over between these scans.
                </Typography>
              ) : (
                delta.unchanged.map((ag, i) => (
                  <AgentDiffCard
                    key={ag.agent_id_hint ?? i}
                    agent={ag}
                    kind="unchanged"
                    onNavigateTopology={onNavigateTopology}
                    compact
                  />
                ))
              )}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Stack>
  )
}

function InventoryColumn({
  title,
  kind,
  count,
  agents,
  emptyText,
  onNavigateTopology,
}: {
  title: string
  kind: 'added' | 'removed' | 'unchanged'
  count: number
  agents: InventoryAgent[]
  emptyText: string
  onNavigateTopology: (agentIdHint: string) => void
}) {
  const accentColor =
    kind === 'added' ? '#388e3c' : kind === 'removed' ? '#d32f2f' : '#9e9e9e'
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="overline" color="text.secondary">
            {title}
          </Typography>
          <Chip
            label={count}
            size="small"
            sx={{
              bgcolor: count === 0 ? undefined : accentColor,
              color: count === 0 ? undefined : '#fff',
            }}
          />
        </Stack>
        {count === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {emptyText}
          </Typography>
        ) : (
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {agents.map((ag, i) => (
              <AgentDiffCard
                key={ag.agent_id_hint ?? i}
                agent={ag}
                kind={kind}
                onNavigateTopology={onNavigateTopology}
              />
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Findings panel — New / Fixed / Unchanged (SD4)
// ─────────────────────────────────────────────────────────────────────

const SEVERITY_COLOR: Record<string, 'error' | 'warning' | 'info' | 'success' | 'default'> = {
  critical: 'error',
  high: 'error',
  medium: 'warning',
  low: 'info',
  info: 'default',
}

function FindingsPanel({
  delta,
  unchanged,
  unchangedLoading,
  unchangedError,
  onLoadUnchanged,
  versionCategory,
  onNavigateAgent,
}: {
  delta: ScanDiffResponse['findings_delta']
  unchanged: FindingDiffRow[] | null
  unchangedLoading: boolean
  unchangedError: string | null
  onLoadUnchanged: () => void
  versionCategory: 'same' | 'patch' | 'minor' | 'major'
  onNavigateAgent: (agentIdHint: string) => void
}) {
  const [subTab, setSubTab] = useState<'new' | 'fixed' | 'unchanged'>('new')

  const handleSubTab = (next: 'new' | 'fixed' | 'unchanged') => {
    setSubTab(next)
    if (next === 'unchanged') onLoadUnchanged()
  }

  const showVersionHint = versionCategory === 'minor' || versionCategory === 'major'

  return (
    <Card variant="outlined">
      <CardContent>
        <Tabs
          value={subTab}
          onChange={(_, v) => handleSubTab(v)}
          sx={{ mb: 2 }}
        >
          <Tab value="new" label={`New (${delta.new_count})`} />
          <Tab value="fixed" label={`Fixed (${delta.fixed_count})`} />
          <Tab value="unchanged" label={`Unchanged (${delta.unchanged_count})`} />
        </Tabs>

        {showVersionHint && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 1.5, color: 'text.secondary' }}
          >
            <InfoIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption">
              {subTab === 'new'
                ? "Some of these 'new' findings may reflect rules added between scanner versions, not new code issues."
                : subTab === 'fixed'
                ? "Some of these 'fixed' findings may reflect rules removed between scanner versions, not actual fixes."
                : 'Unchanged findings are unaffected by scanner version differences.'}
            </Typography>
          </Stack>
        )}

        {subTab === 'new' && (
          <FindingsTable
            rows={delta.new}
            kind="new"
            emptyText="No new findings — nothing got worse."
            onNavigateAgent={onNavigateAgent}
          />
        )}
        {subTab === 'fixed' && (
          <FindingsTable
            rows={delta.fixed}
            kind="fixed"
            emptyText="No findings were fixed."
            onNavigateAgent={onNavigateAgent}
          />
        )}
        {subTab === 'unchanged' && (
          <>
            {unchangedLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {unchangedError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {unchangedError}
              </Alert>
            )}
            {!unchangedLoading && !unchangedError && (
              <FindingsTable
                rows={unchanged ?? []}
                kind="unchanged"
                emptyText="No findings carried over between these scans."
                onNavigateAgent={onNavigateAgent}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function FindingsTable({
  rows,
  kind,
  emptyText,
  onNavigateAgent,
}: {
  rows: FindingDiffRow[]
  kind: 'new' | 'fixed' | 'unchanged'
  emptyText: string
  onNavigateAgent: (agentIdHint: string) => void
}) {
  if (rows.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {emptyText}
        </Typography>
      </Box>
    )
  }

  const accent = kind === 'new' ? '#d32f2f' : kind === 'fixed' ? '#388e3c' : '#9e9e9e'

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 110 }}>Severity</TableCell>
            <TableCell>Title</TableCell>
            <TableCell sx={{ width: 160 }}>Rule</TableCell>
            <TableCell sx={{ width: 180 }}>Agent</TableCell>
            <TableCell>Location</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((f, i) => (
            <TableRow
              key={f.id ?? `${f.rule_id}-${i}`}
              sx={{ borderLeft: `3px solid ${accent}` }}
            >
              <TableCell>
                {f.severity ? (
                  <Chip
                    label={f.severity}
                    size="small"
                    color={SEVERITY_COLOR[f.severity.toLowerCase()] ?? 'default'}
                    sx={{ textTransform: 'capitalize', fontSize: '0.7rem' }}
                  />
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2">{f.title ?? '(no title)'}</Typography>
                {f.framework && (
                  <Typography variant="caption" color="text.secondary">
                    {f.framework}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                {f.rule_id ? (
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                    {f.rule_id}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    —
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                {f.agent_id_hint ? (
                  <Chip
                    label={f.agent_id_hint}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.65rem', maxWidth: 170 }}
                    onClick={() => onNavigateAgent(f.agent_id_hint!)}
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    —
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                {f.file_path ? (
                  <Typography
                    variant="caption"
                    sx={{ fontFamily: 'monospace', fontSize: '0.7rem', wordBreak: 'break-all' }}
                  >
                    {f.file_path}
                    {f.line_number ? `:${f.line_number}` : ''}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    —
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

// ─────────────────────────────────────────────────────────────────────
// ABF Drift panel — per-agent verdicts (SD4)
// ─────────────────────────────────────────────────────────────────────

const VERDICT_FALLBACK: Record<string, { bg: string; label: string }> = {
  POSITIVE_DRIFT: { bg: '#388e3c', label: 'Positive Drift' },
  REBASELINE: { bg: '#1976d2', label: 'Rebaseline' },
}

function VerdictChip({ verdict }: { verdict: string }) {
  // Use DriftBadge for the 4 verdicts it covers — fall back to inline
  // styled chip for POSITIVE_DRIFT + REBASELINE.
  if (['STABLE', 'COSMETIC_DRIFT', 'MATERIAL_DRIFT', 'CRITICAL_DRIFT'].includes(verdict)) {
    return <DriftBadge verdict={verdict} />
  }
  const cfg = VERDICT_FALLBACK[verdict]
  if (!cfg) return <Chip label={verdict} size="small" variant="outlined" />
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{ bgcolor: cfg.bg, color: '#fff', fontSize: '0.7rem' }}
    />
  )
}

function AbfDriftPanel({
  rows,
  abfDiffers,
}: {
  rows: AbfVerdictRow[]
  abfDiffers: boolean
}) {
  const [selected, setSelected] = useState<AbfVerdictRow | null>(null)

  if (rows.length === 0) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            No ABF data available — either neither scan recorded
            fingerprints, or the baseline scan predates ABF v2.
          </Typography>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card variant="outlined">
      <CardContent>
        {abfDiffers && (
          <Alert severity="info" sx={{ mb: 2 }}>
            ABF schema version differs between these scans — every per-agent
            verdict is REBASELINE by policy (P0a auto-rebaseline). The
            baseline fingerprint can't be compared against the current
            schema; a new baseline was recorded at the boundary.
          </Alert>
        )}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Agent</TableCell>
                <TableCell sx={{ width: 120 }}>Framework</TableCell>
                <TableCell sx={{ width: 160 }}>Verdict</TableCell>
                <TableCell sx={{ width: 100 }}>Drift score</TableCell>
                <TableCell>Summary</TableCell>
                <TableCell sx={{ width: 110 }}>Presence</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow
                  key={r.agent_id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setSelected(r)}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {r.agent_name ?? '(unnamed)'}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}
                    >
                      {r.agent_id.slice(0, 12)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {r.framework ? (
                      <Chip
                        label={r.framework}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <VerdictChip verdict={r.verdict} />
                      {r.improvement === 'POSITIVE_DRIFT' && r.verdict !== 'POSITIVE_DRIFT' && (
                        <Chip
                          label="+ improvement"
                          size="small"
                          sx={{ bgcolor: '#388e3c', color: '#fff', fontSize: '0.65rem' }}
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {typeof r.drift_score === 'number'
                      ? r.drift_score.toFixed(2)
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {r.summary ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        r.present_in === 'both'
                          ? 'A + B'
                          : r.present_in === 'a_only'
                          ? 'A only'
                          : 'B only'
                      }
                      size="small"
                      variant="outlined"
                      color={r.present_in === 'both' ? 'default' : 'info'}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>

      <Dialog
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selected?.agent_name ?? '(unnamed)'}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {selected?.agent_id}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1}>
                <VerdictChip verdict={selected.verdict} />
                {selected.framework && (
                  <Chip label={selected.framework} size="small" variant="outlined" />
                )}
                <Chip
                  label={
                    selected.present_in === 'both'
                      ? 'present in A and B'
                      : selected.present_in === 'a_only'
                      ? 'A only'
                      : 'B only'
                  }
                  size="small"
                  variant="outlined"
                />
              </Stack>
              {selected.summary && (
                <Typography variant="body2">{selected.summary}</Typography>
              )}
              <Divider />
              <Typography variant="overline" color="text.secondary">
                Component drifts
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  bgcolor: 'grey.50',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  maxHeight: 320,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {selected.component_drifts && Object.keys(selected.component_drifts).length > 0
                  ? JSON.stringify(selected.component_drifts, null, 2)
                  : '(no component-level detail)'}
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}

function AgentDiffCard({
  agent,
  kind,
  onNavigateTopology,
  compact = false,
}: {
  agent: InventoryAgent
  kind: 'added' | 'removed' | 'unchanged'
  onNavigateTopology: (agentIdHint: string) => void
  compact?: boolean
}) {
  const accentColor =
    kind === 'added' ? '#388e3c' : kind === 'removed' ? '#d32f2f' : '#9e9e9e'
  const displayName =
    agent.agent_name && agent.agent_name !== 'unnamed_agent'
      ? agent.agent_name
      : agent.framework
      ? `${agent.framework} agent`
      : 'agent'

  if (compact) {
    return (
      <Chip
        label={displayName}
        size="small"
        variant="outlined"
        sx={{ borderColor: accentColor, color: 'text.primary' }}
        onClick={
          agent.agent_id_hint
            ? () => onNavigateTopology(agent.agent_id_hint!)
            : undefined
        }
      />
    )
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        minWidth: 240,
        maxWidth: 320,
        flex: '1 1 240px',
        borderLeft: 4,
        borderLeftColor: accentColor,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
            {displayName}
          </Typography>
          {agent.file_path && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: 'monospace', fontSize: '0.65rem', wordBreak: 'break-all' }}
            >
              {agent.file_path}
            </Typography>
          )}
        </Box>
        {agent.agent_id_hint && (
          <Tooltip title="View in topology">
            <IconButton
              size="small"
              onClick={() => onNavigateTopology(agent.agent_id_hint!)}
              sx={{ p: 0.25, flexShrink: 0 }}
            >
              <AccountTreeIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
        {agent.framework && (
          <Chip
            label={agent.framework}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        )}
        {agent.autonomy_level && agent.autonomy_level !== 'L1' && (
          <Chip
            label={agent.autonomy_level}
            size="small"
            color={
              ['L4', 'L5', 'L6'].includes(agent.autonomy_level)
                ? 'error'
                : ['L2', 'L3'].includes(agent.autonomy_level)
                ? 'warning'
                : 'default'
            }
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        )}
        {agent.multi_agent && (
          <Chip
            label="multi-agent"
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        )}
      </Stack>
    </Paper>
  )
}

// ─────────────────────────────────────────────────────────────────────
// §22 — Cost drift panel
// ─────────────────────────────────────────────────────────────────────

function formatCostRange(range: CostRange | null): string {
  if (!range) return '—'
  const fmt = (v: number) => (v < 0.01 ? v.toFixed(4) : v.toFixed(2))
  return `$${fmt(range.low)}–$${fmt(range.high)}`
}

/** Like DeltaChip, but with cost-appropriate semantics: a positive delta
 * means the agent got MORE expensive (warning), not "improved" (success) —
 * DeltaChip's default coloring is inverted for that reason and isn't
 * reused here. */
function CostDeltaChip({ value }: { value: number }) {
  if (value === 0) {
    return <Chip label="±0.0%" size="small" variant="outlined" />
  }
  const increased = value > 0
  return (
    <Chip
      label={`${increased ? '+' : ''}${value.toFixed(1)}%`}
      size="small"
      color={increased ? 'warning' : 'success'}
      variant="outlined"
    />
  )
}

function CostDriftPanel({
  rows,
  onNavigateAgent,
}: {
  rows: CostDeltaAgent[]
  onNavigateAgent: (agentIdHint: string) => void
}) {
  if (rows.length === 0) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            No cost drift to show — either neither scan has a matched cost
            estimate for a shared agent, or nothing moved enough to flag.
          </Typography>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Agent</TableCell>
                <TableCell sx={{ width: 120 }}>Framework</TableCell>
                <TableCell sx={{ width: 160 }}>Cost / request (before)</TableCell>
                <TableCell sx={{ width: 160 }}>Cost / request (after)</TableCell>
                <TableCell sx={{ width: 100 }}>Delta</TableCell>
                <TableCell sx={{ width: 100 }}>Severity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow
                  key={r.agent_id_hint}
                  hover
                  sx={{ cursor: r.agent_id_hint ? 'pointer' : 'default' }}
                  onClick={() => r.agent_id_hint && onNavigateAgent(r.agent_id_hint)}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {r.agent_name ?? '(unnamed)'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {r.framework ? (
                      <Chip label={r.framework} size="small" variant="outlined" color="primary" />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>{formatCostRange(r.cost_per_request_usd_a)}</TableCell>
                  <TableCell>{formatCostRange(r.cost_per_request_usd_b)}</TableCell>
                  <TableCell>
                    <CostDeltaChip value={r.delta_pct_midpoint} />
                  </TableCell>
                  <TableCell>
                    {r.severity ? (
                      <Chip
                        label={r.severity}
                        size="small"
                        color={r.severity === 'critical' ? 'error' : 'warning'}
                      />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  )
}
